import type {
  BindersResponse,
  BinderResponse,
  BinderPageResponse,
  BinderSlotResponse,
  ShareTokenResponse,
  SharedBinderResponse,
  BinderLayout,
} from '@/types'
import { request } from './client'

export async function getBinders(): Promise<BindersResponse> {
  return request<BindersResponse>('/binders')
}

export async function getBinder(id: number): Promise<BinderResponse> {
  return request<BinderResponse>(`/binders/${id}`)
}

export async function createBinder(
  data: {
    name: string
    description?: string
    layout: BinderLayout
    coverColor?: string
  },
  idempotencyKey?: string,
): Promise<BinderResponse> {
  return request<BinderResponse>('/binders', {
    method: 'POST',
    body: data,
    idempotencyKey,
  })
}

export async function updateBinder(
  id: number,
  data: { name?: string; description?: string; layout?: BinderLayout; coverColor?: string },
  idempotencyKey?: string,
): Promise<BinderResponse> {
  return request<BinderResponse>(`/binders/${id}`, {
    method: 'PATCH',
    body: data,
    idempotencyKey,
  })
}

export async function deleteBinder(id: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/binders/${id}`, { method: 'DELETE', idempotencyKey })
}

export async function regenerateBinderShareToken(id: number, idempotencyKey?: string): Promise<ShareTokenResponse> {
  return request<ShareTokenResponse>(`/binders/${id}/regenerate_token`, { method: 'POST', idempotencyKey })
}

export async function addBinderPage(id: number, idempotencyKey?: string): Promise<BinderPageResponse> {
  return request<BinderPageResponse>(`/binders/${id}/pages`, { method: 'POST', idempotencyKey })
}

export async function removeBinderPage(id: number, pageNumber: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/binders/${id}/pages/${pageNumber}`, { method: 'DELETE', idempotencyKey })
}

export async function setBinderSlot(
  id: number,
  pageNumber: number,
  position: number,
  cardId: number,
  idempotencyKey?: string,
): Promise<BinderSlotResponse> {
  return request<BinderSlotResponse>(`/binders/${id}/pages/${pageNumber}/slots/${position}`, {
    method: 'PUT',
    body: { cardId },
    idempotencyKey,
  })
}

export async function clearBinderSlot(
  id: number,
  pageNumber: number,
  position: number,
  idempotencyKey?: string,
): Promise<void> {
  await request<void>(`/binders/${id}/pages/${pageNumber}/slots/${position}`, {
    method: 'DELETE',
    idempotencyKey,
  })
}

export async function moveBinderSlot(
  id: number,
  args: { fromPageNumber: number; fromPosition: number; toPageNumber: number; toPosition: number },
  idempotencyKey?: string,
): Promise<BinderResponse> {
  return request<BinderResponse>(`/binders/${id}/move_slot`, {
    method: 'POST',
    body: args,
    idempotencyKey,
  })
}

export async function getSharedBinder(shareToken: string): Promise<SharedBinderResponse> {
  return request<SharedBinderResponse>(`/shared/binders/${shareToken}`)
}
